import { useState } from "react";
import { ArrowRight, Heart, Instagram, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { Logo } from "@/sections/Nav";

export default function Footer() {
  const [email, setEmail] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Hmm, that email looks half-baked", { description: "Double-check it and try again." });
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/signups`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (res.status === 409) {
        toast("You're already on the list!", { description: "Our kitchen letters are already headed your way." });
      } else if (res.ok) {
        toast.success("You're on the list!", {
          description: "New flavors, recipes and subscriber-only treats — coming your way.",
        });
      } else {
        throw new Error();
      }
      setEmail("");
    } catch {
      toast.error("The oven door jammed", { description: "Couldn't save your signup — please try again." });
    }
  };

  return (
    <footer id="contact" className="bg-[#2C221E] text-[#FAF7F2] pt-24 sm:pt-32 pb-10 relative overflow-hidden">
      <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#6A2C3E]/40 blur-[110px]" />
      <div className="absolute -bottom-32 -left-24 w-[420px] h-[420px] rounded-full bg-[#2A524A]/40 blur-[110px]" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
        <Reveal className="max-w-3xl">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#C86D51] mb-6">Letters from Our Kitchen</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.08]">
            Fresh bakes,<br />straight to <em className="italic text-[#F8C8DC]">your inbox</em>
          </h2>
          <p className="mt-6 text-[#FAF7F2]/60 leading-relaxed max-w-xl">
            New flavors, recipes worth keeping, and subscriber-only treats — from our
            kitchen to your inbox, about once a month. No spam, just the good stuff.
          </p>

          <form onSubmit={submit} className="mt-9 flex flex-col sm:flex-row gap-3 max-w-lg" data-testid="newsletter-form">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="newsletter-email-input"
              className="flex-1 rounded-full bg-[#FAF7F2]/10 border border-[#FAF7F2]/20 px-6 py-4 text-sm text-[#FAF7F2] placeholder:text-[#FAF7F2]/40 outline-none focus:border-[#F8C8DC]/60 focus:bg-[#FAF7F2]/15 transition-all duration-300"
            />
            <button
              type="submit"
              data-testid="newsletter-submit-button"
              className="rounded-full bg-[#F8C8DC] text-[#6A2C3E] px-8 py-4 text-sm font-semibold inline-flex items-center justify-center gap-2 hover:bg-[#FAF7F2] hover:scale-[1.03] active:scale-[0.97] transition-all duration-300"
            >
              Count me in <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          <div className="mt-5 flex items-center gap-4 max-w-lg">
            <span className="h-px flex-1 bg-[#FAF7F2]/10" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FAF7F2]/40">or</span>
            <span className="h-px flex-1 bg-[#FAF7F2]/10" />
          </div>
          <a
            href="https://wa.me/918886016001?text=Hi%20Almo%20Bakes!%20I%20have%20a%20question%20about%20your%20mixes."
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-chat-button"
            className="mt-5 inline-flex items-center gap-2.5 rounded-full border border-[#FAF7F2]/25 px-8 py-4 text-sm font-medium text-[#FAF7F2] hover:bg-[#D1E8E2] hover:text-[#2A524A] hover:border-[#D1E8E2] hover:scale-[1.03] active:scale-[0.97] transition-all duration-300"
          >
            <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
          </a>
        </Reveal>

        <div className="mt-24 pt-10 border-t border-[#FAF7F2]/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <Logo dark />
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
            <a
              href="mailto:info@almobakes.com"
              data-testid="footer-email-link"
              className="inline-flex items-center gap-2 text-sm text-[#FAF7F2]/70 hover:text-[#F8C8DC] transition-colors"
            >
              <Mail className="w-4 h-4" /> info@almobakes.com
            </a>
            <a
              href="https://instagram.com/almo.bakes"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram-link"
              className="inline-flex items-center gap-2 text-sm text-[#FAF7F2]/70 hover:text-[#F8C8DC] transition-colors"
            >
              <Instagram className="w-4 h-4" /> @almo.bakes
            </a>
          </div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FAF7F2]/40 inline-flex items-center gap-1.5">
            © 2026 Almo Bakes · Baked with <Heart className="w-3 h-3 text-[#C86D51] fill-current" />
          </p>
        </div>
      </div>
    </footer>
  );
}
